// Bottom sheet shown when the user taps a bar on /stats/week. Mirrors the
// /growth timeline view: a TimelineSpine of every event that happened on the
// tapped day, with a compact stat summary up top.

import type {
  AnyEvent,
  DiaperEvent,
  FeedingSession,
  PumpingSession,
  SleepSession,
} from '@shared/types.ts';
import { SheetShell } from './SheetShell.tsx';
import { Icon } from '../Icon.tsx';
import { TimelineSpine, type SpineItem } from '../growth/TimelineSpine.tsx';
import { fmtTime, fmtDur } from '../../lib/format.ts';
import { computeDayStats, startOfDaySec } from '../../lib/stats.ts';
import { summarise, summaryLabel } from '../../lib/breast.ts';

type Props = {
  open: boolean;
  date: Date | null;
  sleep: SleepSession[];
  feed: FeedingSession[];
  pump: PumpingSession[];
  diaper: DiaperEvent[];
  onClose: () => void;
  onSelectEvent?: (e: AnyEvent) => void;
};

const DAY = 86400;

export function DayTimelineSheet({
  open,
  date,
  sleep,
  feed,
  pump,
  diaper,
  onClose,
  onSelectEvent,
}: Props) {
  if (!date) return null;

  const dayStart = startOfDaySec(date);
  const dayEnd = dayStart + DAY;
  const stats = computeDayStats({ sleep, feed, pump, diaper, date });

  // Anchor every event by its start (or occurred_at for diaper). A bedtime
  // that crosses into tomorrow morning still belongs to today — matches how
  // the user thinks about "stuff that happened on Wednesday".
  const inDay = (anchor: number): boolean => anchor >= dayStart && anchor < dayEnd;

  const events: AnyEvent[] = [
    ...sleep.filter((s) => inDay(s.started_at)).map((s) => ({ ...s, _t: 'sleep' as const })),
    ...feed.filter((f) => inDay(f.started_at)).map((f) => ({ ...f, _t: 'feed' as const })),
    ...pump.filter((p) => inDay(p.started_at)).map((p) => ({ ...p, _t: 'pump' as const })),
    ...diaper.filter((d) => inDay(d.occurred_at)).map((d) => ({ ...d, _t: 'diaper' as const })),
  ];

  const nowSec = Math.floor(Date.now() / 1000);
  const items: SpineItem[] = events.map((e) => {
    const at = e._t === 'diaper' ? e.occurred_at : e.started_at;
    // Sleep / breast / pump have a meaningful duration to render as a
    // rail segment. Diaper, bottle, and solid are point-in-time.
    let endAt: number | undefined;
    if (e._t === 'sleep') endAt = e.ended_at ?? nowSec;
    else if (e._t === 'pump') endAt = e.ended_at ?? nowSec;
    else if (e._t === 'feed' && (e.type === 'breast_left' || e.type === 'breast_right')) {
      endAt = e.ended_at ?? nowSec;
    }
    const meta = describe(e);
    return {
      key: e.id,
      at,
      endAt,
      accent: `var(--${meta.accent})`,
      render: () => (
        <button
          type="button"
          onClick={() => onSelectEvent?.(e)}
          style={{
            all: 'unset',
            width: '100%',
            boxSizing: 'border-box',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '8px 4px',
            cursor: onSelectEvent ? 'pointer' : 'default',
          }}
        >
          <meta.Icn s={16} strokeWidth={1.8} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 14,
                fontWeight: 500,
                color: 'var(--text-1)',
                letterSpacing: -0.005,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {meta.title}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 1 }} className="lt-tabular">
              {endAt && endAt > at + 30
                ? `${fmtTime(at)} → ${e._t !== 'diaper' && e.ended_at == null ? 'now' : fmtTime(endAt)}`
                : fmtTime(at)}
              {meta.subtitle && ` · ${meta.subtitle}`}
            </div>
          </div>
        </button>
      ),
    };
  });

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const yesterday = today.getTime() - DAY * 1000;
  const dateMs = new Date(date).setHours(0, 0, 0, 0);
  const dayLabel =
    dateMs === today.getTime()
      ? 'Today'
      : dateMs === yesterday
        ? 'Yesterday'
        : new Date(date).toLocaleDateString(undefined, {
            weekday: 'long',
            day: 'numeric',
            month: 'short',
          });

  const totalSleep = stats.sleepTotalMin > 0 ? fmtDur(stats.sleepTotalMin) : '0';

  return (
    <SheetShell
      open={open}
      onClose={onClose}
      title={dayLabel}
      tagline={`${totalSleep} sleep · ${stats.feedCount} feed${stats.feedCount === 1 ? '' : 's'} · ${stats.diaperCount} diaper${stats.diaperCount === 1 ? '' : 's'}`}
      accent="accent"
      icon={(p) => <Icon.clock {...p} />}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <Mini label="Last night" value={stats.lastNightMin > 0 ? fmtDur(stats.lastNightMin) : '—'} color="sleep-night" />
          <Mini label="Naps" value={stats.napsMin > 0 ? fmtDur(stats.napsMin) : '—'} color="sleep-day" sub={`${stats.naps} session${stats.naps === 1 ? '' : 's'}`} />
          <Mini label="Longest" value={stats.longestStretchMin > 0 ? fmtDur(stats.longestStretchMin) : '—'} />
          <Mini label="Bottle" value={stats.bottleMl > 0 ? `${stats.bottleMl} ml` : '—'} color="bottle" />
        </div>

        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: 'var(--text-3)',
            textTransform: 'uppercase',
            letterSpacing: 0.04,
            paddingLeft: 4,
            marginTop: 4,
          }}
        >
          Events
        </div>
        {items.length === 0 ? (
          <div
            style={{
              padding: '20px 8px',
              textAlign: 'center',
              color: 'var(--text-3)',
              fontSize: 13,
            }}
          >
            Nothing logged on this day.
          </div>
        ) : (
          <TimelineSpine items={items} scale="day" />
        )}
      </div>
    </SheetShell>
  );
}

function Mini({
  label,
  value,
  sub,
  color,
}: {
  label: string;
  value: string;
  sub?: string;
  color?: string;
}) {
  return (
    <div
      style={{
        padding: '10px 12px',
        borderRadius: 14,
        background: 'var(--bg-card)',
        border: '1px solid var(--border)',
      }}
    >
      <div
        style={{
          fontSize: 10,
          fontWeight: 600,
          color: 'var(--text-3)',
          textTransform: 'uppercase',
          letterSpacing: 0.04,
        }}
      >
        {label}
      </div>
      <div
        className="lt-serif lt-tabular"
        style={{
          fontSize: 18,
          lineHeight: 1.1,
          marginTop: 4,
          color: color ? `var(--${color})` : 'var(--text-1)',
        }}
      >
        {value}
      </div>
      {sub && <div style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function describe(e: AnyEvent): {
  title: string;
  subtitle?: string;
  accent: string;
  Icn: (p: { s: number; strokeWidth?: number }) => React.ReactNode;
} {
  if (e._t === 'sleep') {
    const dur = e.ended_at
      ? fmtDur(Math.max(0, Math.floor((e.ended_at - e.started_at) / 60)))
      : 'in progress';
    return {
      title: e.location ? `Sleep · ${e.location}` : 'Sleep',
      subtitle: dur,
      accent: 'sleep-night',
      Icn: Icon.moon,
    };
  }
  if (e._t === 'feed') {
    if (e.type === 'bottle') {
      return {
        title: e.amount_ml ? `Bottle · ${e.amount_ml} ml` : 'Bottle',
        subtitle: e.content ?? undefined,
        accent: 'bottle',
        Icn: Icon.bottle,
      };
    }
    if (e.type === 'solid') {
      return { title: 'Solid', accent: 'solid', Icn: Icon.solid };
    }
    const s = summarise(e);
    const label = s.hasHistory ? summaryLabel(s) : e.type === 'breast_left' ? 'L' : 'R';
    const dur = e.ended_at
      ? fmtDur(Math.max(1, Math.round((e.ended_at - e.started_at) / 60)))
      : 'in progress';
    return {
      title: `Breast · ${label}`,
      subtitle: dur,
      accent: 'breast',
      Icn: Icon.breast,
    };
  }
  if (e._t === 'pump') {
    const dur = e.ended_at
      ? fmtDur(Math.max(0, Math.floor((e.ended_at - e.started_at) / 60)))
      : 'in progress';
    return {
      title: e.yield_ml != null ? `Pump · ${e.yield_ml} ml` : 'Pump',
      subtitle: dur,
      accent: 'pump',
      Icn: Icon.pump,
    };
  }
  return {
    title: `Diaper · ${e.kind}`,
    subtitle: e.consistency ?? undefined,
    accent: e.kind === 'pee' ? 'pee' : 'poop',
    Icn: Icon.diaper,
  };
}
